import { SiteLayout } from "../components/site/SiteLayout";
import { SEO } from "../components/site/SEO";
import { ContactForm } from "../components/site/ContactForm";

const STEPS = [
  {
    index: "01",
    title: "Understand your business",
    body:
      "We learn about your dairy, delivery areas, existing customers and growth ambitions. No boilerplate — just the specifics of what you deliver, where, and to whom.",
  },
  {
    index: "02",
    title: "Build your local presence",
    body:
      "Improve the website, set up or tidy up your Facebook, and create the content and creative that will do the heavy lifting across the local community groups and targeted ads.",
  },
  {
    index: "03",
    title: "Support your growth",
    body:
      "Deliver the ongoing marketing activity, monitor what's working, and keep making practical improvements month on month. Honest reporting, no vanity metrics.",
  },
];

const INCLUDES = [
  "Managing your dairy's Facebook presence day to day",
  "Regular, professional posts written in your voice",
  "Website build or improvements to the one you already have",
  "Advertising within appropriate local community groups",
  "Running targeted local Facebook advertising campaigns",
  "Promoting products, delivery areas and seasonal offers",
  "Helping generate new customer enquiries you can convert",
];

export default function DairyGrowth() {
  return (
    <SiteLayout>
      <SEO
        title="Dairy Growth & Marketing — Fourbees Consultancy"
        description="Monthly marketing support for independent dairies and milkmen. Facebook management, website improvements, targeted local advertising and customer acquisition done properly."
        path="/dairy-growth"
      />

      {/* Hero */}
      <section className="bg-cream py-24 md:py-32" data-testid="dairy-hero">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="section-number mb-3">For independent dairies</div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold text-navy leading-[1.0] tracking-[-0.025em]">
                More customers. <br />
                Stronger rounds. <br />
                <span className="text-gold">Less hassle.</span>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="font-body text-lg md:text-xl text-navy font-semibold leading-snug">
                You concentrate on delivering great service. We'll help more local
                households discover it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="bg-cream border-t border-light-grey py-24 md:py-28" data-testid="dairy-includes">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="section-number mb-3">01 — Monthly support</div>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-navy leading-[1.05] tracking-tight">
                What you can expect every month.
              </h2>
              <p className="font-body text-charcoal/70 mt-6 leading-relaxed">
                A single engagement that covers the local marketing side of the business —
                content, website, community presence and targeted advertising. All planned
                around your round, not generic templates.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="divide-y divide-light-grey border-t border-light-grey">
                {INCLUDES.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-5 py-5"
                    data-testid={`dairy-include-${i}`}
                  >
                    <span className="section-number flex-shrink-0 mt-1">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-body text-base md:text-lg text-charcoal/85 leading-relaxed">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-navy text-cream py-24 md:py-28" data-testid="dairy-process">
        <div className="container-site">
          <div className="section-number mb-3">02 — How it works</div>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-cream leading-[1.05] tracking-tight mb-14 max-w-2xl">
            A simple three-stage approach.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div
                key={i}
                className="border border-cream/15 p-8 md:p-10 rounded-sm hover:border-gold transition-colors"
                data-testid={`dairy-step-${i}`}
              >
                <div className="font-heading text-5xl md:text-6xl font-extrabold text-gold leading-none mb-6">
                  {s.index}
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-bold text-cream leading-tight mb-4">
                  {s.title}
                </h3>
                <p className="font-body text-sm md:text-base text-cream/70 leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-xs text-cream/50 max-w-3xl">
            A note on expectations: we don't promise guaranteed customers, sales or return
            on advertising spend. We commit to doing the work properly, reporting honestly
            on what's happening, and adjusting as we go.
          </p>
        </div>
      </section>

      {/* Enquiry form */}
      <section className="bg-cream py-24 md:py-28" data-testid="dairy-form">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="section-number mb-3">03 — Dairy marketing enquiry</div>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-navy leading-[1.05] tracking-tight mb-6">
                Tell us a bit about your round.
              </h2>
              <p className="font-body text-charcoal/70 leading-relaxed">
                A short note is enough to start. We'll come back to understand the detail
                and talk through whether we can help.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ContactForm defaultService="Local Advertising & Customer Growth" />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
